import { useEffect, useRef, useState } from 'react';
import { sendToNative, isNative } from '@/shared/bridge';

/** 새로고침 발화 임계 거리(px) — 이만큼 당겨야 `onRefresh` 실행. */
const THRESHOLD = 80;
/** 당김 상한(px) — 실제로 닿지 않고 **점근**한다(`damp` 참고). */
const MAX_PULL = 160;
/** 기본 저항 — 임계 전 구간의 손가락 이동 대비 이동 비율(1 = 1:1, 낮을수록 뻑뻑). */
const RESISTANCE = 0.3;
/** 세로 당김으로 인정할 최소 우세 각도 — |dy| 가 |dx| 의 이 배수 이상이어야 가로 스와이프와 구분. */
const VERTICAL_BIAS = 1.5;
/**
 * "최상단" 판정 허용치(px) — `scrollTop === 0` 을 엄격히 요구하면 실기기에서 자주 실패한다: 서브픽셀 스크롤
 * (DPR·확대), 관성 종료 직후의 잔여 오차, iOS 바운스 복귀 중의 소수값 때문에 시각적으로 top 인데 0.5~1 이
 * 남는 경우가 흔하다. 몇 px 여유를 주면 사용자가 top 이라 인식하는 상태와 판정이 일치한다.
 */
const TOP_TOLERANCE = 2;
/**
 * 스피너 최소 유지 시간(ms) — 캐시 히트·빠른 응답이면 `onRefresh` 가 수십 ms 만에 끝나 스피너가 "깜빡"하고
 * 사라진다. 사용자는 새로고침이 실제로 돌았는지 확인할 수 없고 제스처가 씹힌 것처럼 보인다. 실제 소요가
 * 이보다 짧으면 남은 시간만큼 더 유지한다(길면 그대로 — 늘리지 않는다).
 */
const MIN_REFRESH_DURATION = 1000;

/**
 * 고무줄 감쇠 — 손가락 이동(dy) → 실제 당김 거리.
 *
 * 임계까지는 **선형**(`RESISTANCE`)이라 발화 지점까지의 감도가 예측 가능하고, 임계를 넘어서면 초과분이
 * **지수적으로 감쇠**하며 `MAX_PULL` 에 점근한다 — 상한에 닿을수록 점점 뻑뻑해지되 **멈추지는 않아** 끝까지
 * 손가락을 미세하게 따라온다(iOS rubber-band 정합).
 *
 * 예전엔 `Math.min(dy * RESISTANCE, MAX_PULL)` 하드 클램프라 상한에서 값이 그대로 굳어 "툭" 끊긴 느낌이었다.
 */
function damp(dy: number): number {
  const raw = dy * RESISTANCE;
  if (raw <= THRESHOLD) return raw;
  const range = MAX_PULL - THRESHOLD;
  // 초과분 over 가 커질수록 (1 - e^-x) 가 1 에 수렴 → 증가폭이 계속 줄지만 0 이 되진 않는다.
  return THRESHOLD + range * (1 - Math.exp(-(raw - THRESHOLD) / range));
}

export interface PullToRefreshState {
  /** 현재 당김 거리(px, 저항 적용 후). */
  pull: number;
  /**
   * 본문을 실제로 밀어내릴 거리(px) — 당기는 중엔 `pull`, 새로고침 중엔 임계값에 **고정**(스피너가 계속 보이게).
   * 소비처가 콘텐츠 `translateY` 와 인디케이터 영역 높이에 동일 적용해 iOS 처럼 "본문이 끌려 내려가고 그 뒤가 드러남".
   */
  offset: number;
  /** 임계 대비 진행률 0~1 — 인디케이터 투명도·크기·회전 램프용(새로고침 중 1). */
  progress: number;
  /** 임계 도달 — "놓으면 새로고침" 상태(색 전환). */
  armed: boolean;
  /** `onRefresh` 실행 중 — 스피너 유지 + 중복 발화 차단. */
  refreshing: boolean;
  /**
   * 손가락이 닿아 당기는 중 — 소비처가 이때만 `transition` 을 꺼서 손가락을 1:1 추종시키고, 놓는 순간
   * 다시 켜서 스냅(복귀/임계 고정)이 애니메이션되게 한다.
   */
  dragging: boolean;
}

/**
 * Pull-to-refresh 제스처 훅 — **터치 환경 전용**(마우스 미지원). 문서가 아니라 **공용 `ScrollArea` 뷰포트**가
 * 스크롤하는 구조라(app-layout) 브라우저·WebView 기본 PTR 은 발화하지 않는다(+ 뷰포트 `overscroll-contain`).
 * 그래서 뷰포트의 터치 오버스크롤을 직접 읽어 제스처를 만든다. 전체 리로드(SPA 재부팅·auth 재hydration) 대신
 * 소비처가 넘긴 `onRefresh`(보통 활성 쿼리 refetch)만 돌린다.
 *
 * 뷰포트는 heritage `ScrollArea` 가 내부 ref 로만 들고 있어 밖에서 못 받는다 → `containerRef`(ScrollArea 를 감싼
 * 노드) 기준 `querySelector('[data-slot=scroll-area-viewport]')` 로 해소한다.
 *
 * **최상단(`TOP_TOLERANCE` 이내)에서의 아래방향** 터치만 인정하고, 가로 우세 제스처(캐러셀·표 가로 스크롤)는
 * 무시한다. top 판정은 touchstart 한 번이 아니라 **매 touchmove 마다** 다시 한다 — iOS 관성 스크롤이 남아
 * 있으면 시작 시점엔 top 이 아니어서, 한 번 실격시키면 사용자가 top 이라 믿고 당겨도 영영 반응하지 않는다.
 * 임계 도달 순간 네이티브면 햅틱(`haptics.impact` light) 한 번 — 브리지에 이미 있는 메시지라 신규 계약 불필요.
 */
export function usePullToRefresh(
  containerRef: React.RefObject<HTMLElement | null>,
  onRefresh: () => Promise<unknown> | unknown,
  enabled = true,
): PullToRefreshState {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [dragging, setDragging] = useState(false);
  // 최신 콜백을 effect 재등록 없이 참조(소비처가 인라인 함수를 넘겨도 리스너가 매번 재바인딩되지 않게).
  // 갱신은 **커밋 후**(effect) — 렌더 중 ref 쓰기는 concurrent 렌더에서 안전하지 않다(react-hooks/refs).
  const onRefreshRef = useRef(onRefresh);
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  });
  // 진행 중 새로고침을 리스너에서 동기 확인(state 는 클로저 캡처라 최신값 보장 안 됨).
  const refreshingRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    const container = containerRef.current;
    const viewport = container?.querySelector<HTMLElement>('[data-slot="scroll-area-viewport"]');
    if (!viewport) return;

    // 네이티브 바운스는 **끄지 않는다**(`ScrollArea` 의 `overscroll-contain` 그대로) — 바운스 감각을 유지하기
    // 위해서다. 대신 당김으로 판정된 첫 move 에서 곧바로 `preventDefault()` 해 브라우저가 상단 바운스를
    // 시작하기 전에 제스처를 가져온다(리스너가 `passive:false` 라 가능). 그래도 iOS 에서 간헐적으로 뺏기면
    // `overscroll-behavior-y: none` 이 확실한 해법이지만, 하단 바운스까지 사라지는 대가가 있다.
    // 제스처 1회분 상태 — touchstart 에서 세팅, end/cancel 에서 초기화.
    let startX = 0;
    let startY = 0;
    let tracking = false; // 단일 터치 시퀀스가 진행 중인가(당김 후보)
    let pulling = false; // 세로 우세가 확정돼 당김으로 승격됐는가
    let currentPull = 0; // 최신 당김 거리 — **놓는 순간** 임계 판정의 기준(state 는 리스너에서 최신값 보장 안 됨)
    let hapticFired = false; // 이번 임계 진입에서 햅틱을 이미 울렸는가(임계 아래로 내려오면 해제 → 재진입 시 재발화)

    const reset = () => {
      tracking = false;
      pulling = false;
      currentPull = 0;
      hapticFired = false;
      setPull(0);
      setDragging(false);
    };

    const onTouchStart = (e: TouchEvent) => {
      // 새로고침 중이어도 **추적은 시작한다** — touchmove 에서 제스처를 삼켜야(네이티브 바운스 차단) 하고,
      // 로딩이 끝나는 순간 손을 떼지 않아도 그대로 당김으로 이어져야 하기 때문(여기서 막으면 둘 다 불가).
      if (e.touches.length !== 1) return;
      const touch = e.touches[0];
      if (!touch) return;
      // 단일 터치면 일단 후보로 둔다. **top 여부는 여기서 확정하지 않는다** — iOS 관성 스크롤이 굴러가는 중에
      // 손을 대면 이 시점 scrollTop 이 아직 > 0 이라, 여기서 실격시키면 사용자가 화면상 top 이라 믿고 당겨도
      // 영영 반응하지 않는다. 실제 판정은 아래 touchmove 에서 매번 다시 한다.
      tracking = true;
      startX = touch.clientX;
      startY = touch.clientY;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!tracking) return;
      const touch = e.touches[0];
      if (!touch) return;
      // 아직 최상단이 아니면(관성 잔여 포함) 당김을 시작하지 않고 **기준점만 현재 위치로 갱신**한다.
      // → 스크롤이 top 에 닿는 순간부터 그 지점을 0 으로 삼아 당김이 자연스럽게 이어진다. 진행 중이던 당김은
      //   되돌린다(사용자가 다시 위로 스크롤한 것).
      if (viewport.scrollTop > TOP_TOLERANCE) {
        if (pulling) {
          pulling = false;
          currentPull = 0;
          hapticFired = false;
          setPull(0);
          setDragging(false);
        }
        startX = touch.clientX;
        startY = touch.clientY;
        return;
      }
      const dy = touch.clientY - startY;
      const dx = touch.clientX - startX;
      // 위로 스크롤하거나 가로가 우세하면 이번 제스처는 당김이 아니다(한 번 탈락하면 끝까지 무시).
      if (dy <= 0 || Math.abs(dy) < Math.abs(dx) * VERTICAL_BIAS) {
        if (!pulling) tracking = false;
        return;
      }
      if (!pulling) setDragging(true); // 승격 시 1회만 — 소비처 transition off(손가락 1:1 추종).
      pulling = true;
      const distance = damp(dy);
      currentPull = distance;
      setPull(distance);
      if (distance >= THRESHOLD) {
        if (!hapticFired) {
          hapticFired = true;
          // 임계 **진입 순간** 촉각 피드백 — 네이티브 셸일 때만(웹은 무음). 실패해도 제스처엔 영향 없음.
          // 이미 로딩 중이면 놓아도 발화하지 않으므로 진동도 울리지 않는다(없는 액션을 예고하지 않게).
          if (!refreshingRef.current && isNative()) {
            sendToNative({ type: 'haptics.impact', payload: { style: 'light' } });
          }
        }
      } else {
        // 임계 아래로 되돌아오면 무장 해제 — 다시 넘으면 햅틱이 또 울린다(색 전환과 동기).
        hapticFired = false;
      }
      // 당기는 동안엔 뷰포트가 같이 튀지 않게 기본 스크롤 억제(리스너가 passive:false 라 가능).
      e.preventDefault();
    };

    const onTouchEnd = () => {
      if (!pulling) return reset();
      // 판정은 **놓는 순간의 실제 거리** — 한 번이라도 임계를 넘었는지(래치)가 아니다. 임계를 넘겼다가 다시
      // 위로 당겨 올려 놓으면 발화하지 않아야 한다(사용자가 취소한 것).
      // 이미 로딩 중이면 당김·시각 피드백은 정상 동작하되 **발화만** 막는다(중복 요청 방지).
      const shouldRefresh = !refreshingRef.current && currentPull >= THRESHOLD;
      reset();
      if (!shouldRefresh) return;
      refreshingRef.current = true;
      setRefreshing(true);
      void (async () => {
        const startedAt = Date.now();
        try {
          await onRefreshRef.current();
        } finally {
          // 실패해도 동일하게 최소 시간을 채운다 — 에러 여부와 무관하게 스피너 수명은 일정해야 한다.
          const remaining = MIN_REFRESH_DURATION - (Date.now() - startedAt);
          if (remaining > 0) await new Promise((resolve) => setTimeout(resolve, remaining));
          refreshingRef.current = false;
          setRefreshing(false);
        }
      })();
    };

    // touchmove 는 preventDefault 를 쓰므로 반드시 passive:false.
    viewport.addEventListener('touchstart', onTouchStart, { passive: true });
    viewport.addEventListener('touchmove', onTouchMove, { passive: false });
    viewport.addEventListener('touchend', onTouchEnd);
    viewport.addEventListener('touchcancel', reset);
    return () => {
      viewport.removeEventListener('touchstart', onTouchStart);
      viewport.removeEventListener('touchmove', onTouchMove);
      viewport.removeEventListener('touchend', onTouchEnd);
      viewport.removeEventListener('touchcancel', reset);
    };
  }, [containerRef, enabled]);

  // 새로고침 중엔 임계 위치를 **바닥으로 유지** — 본문이 내려간 채 스피너가 계속 보이고(iOS 정합), 그 상태에서
  // 더 당기면 손가락을 그대로 따라간다(로딩 중 당김도 동일 감각). 놓으면 다시 임계로, 로딩이 끝나면 0 으로 스냅.
  const offset = refreshing ? Math.max(THRESHOLD, pull) : pull;
  return {
    pull,
    offset,
    progress: refreshing ? 1 : Math.min(pull / THRESHOLD, 1),
    armed: pull >= THRESHOLD,
    refreshing,
    dragging,
  };
}

/** 인디케이터가 100% 드러나는 기준 거리 — 소비처가 진행률(0~1) 산출에 쓴다. */
export const PULL_TO_REFRESH_THRESHOLD = THRESHOLD;
